import React, { useState, useEffect, useRef } from 'react';
import { Megaphone, Plus, ExternalLink, Settings, X as XIcon, Search, Eye, EyeOff, RefreshCw, Copy, Trash2, Mail, Download } from 'lucide-react';
import api from '../../api/axios';
import DataGrid, { GridColumn } from '../../components/DataGrid';
import Modal from '../../components/Modal';

/* ── Types ───────────────────────────────────────────────────────────────── */

type MetaProduct = 'NEXORA' | 'KULALI' | 'PENTAFLOW';
type Objective = 'LEAD_GENERATION' | 'TRAFFIC' | 'ENGAGEMENT';
type DestinationType = 'LEAD_FORM' | 'LANDING_URL';
type PlacementMode = 'ADVANTAGE_PLUS' | 'MANUAL_FEED_ONLY';

interface MetaProductConfigDTO {
  product: MetaProduct;
  appId?: string;
  adAccountId?: string;
  pageId?: string;
  accessTokenSet: boolean;
  appSecretSet: boolean;
  webhookVerifyTokenSet: boolean;
  privacyPolicyUrl?: string;
  configured: boolean;
}

interface MetaCampaignDTO {
  id: string;
  product: MetaProduct;
  objective: string;
  campaignName: string;
  status: 'DRAFT' | 'CREATED' | 'FAILED' | 'IMPORTED' | 'DELETED';
  metaCampaignId?: string;
  metaAdSetId?: string;
  metaAdId?: string;
  metaCreativeId?: string;
  metaFormId?: string;
  dailyBudgetInr?: number;
  lifetimeBudgetInr?: number;
  targetCity?: string;
  targetState?: string;
  errorMessage?: string;
  createdAt: string;
  /** Raw CampaignCreateRequest JSON — only present for campaigns created through Nexora, powers Duplicate. */
  requestPayload?: string;
  emailSubject?: string;
  emailBodyHtml?: string;
}

/** Mirrors the backend's CampaignCreateRequest shape (see CampaignCreateRequest.java) — parsed from requestPayload to pre-fill the Create modal when duplicating. */
interface DuplicateCampaignPayload {
  product: MetaProduct;
  objective: string;
  budget: { dailyInr?: number; lifetimeInr?: number };
  targeting: {
    locations: { city: string; state?: string; radiusKm?: number }[];
    ageMin?: number; ageMax?: number; gender?: string; interestIds?: string[];
  };
  schedule: { startAt?: string; endAt?: string };
  creative: {
    primaryText?: string; headline?: string; description?: string; ctaType?: string;
    assetRef?: string; destinationType?: string; destinationUrl?: string;
  };
  placement?: { mode?: string };
  leadForm?: { formName: string; fields: { type: string; question?: string }[]; privacyPolicyUrl?: string };
}

interface CampaignCreateResponse {
  id: string;
  metaCampaignId?: string;
  metaAdSetId?: string;
  metaAdId?: string;
  metaCreativeId?: string;
  metaFormId?: string;
  adsManagerUrl: string;
  status: string;
}

interface InterestSearchResultDTO {
  id: string;
  name: string;
  audienceSizeLowerBound?: number;
  audienceSizeUpperBound?: number;
}

interface LeadFormFieldDraft {
  type: string;
  question: string;
}

interface LocationDraft {
  city: string;
  state: string;
  radiusKm: string;
}

const AD_FORMATS = ['MOBILE_FEED_STANDARD', 'DESKTOP_FEED_STANDARD', 'INSTAGRAM_STANDARD', 'INSTAGRAM_STORY'];

type Envelope<T> = { success?: boolean; data?: T } | T;
function unwrap<T>(raw: Envelope<T>): T {
  return (raw as { data?: T })?.data ?? (raw as T);
}
function errorMessage(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { message?: string; detail?: string } } })?.response?.data;
  return msg?.message || msg?.detail || fallback;
}

/* ── Static option lists ────────────────────────────────────────────────── */

const PRODUCTS: MetaProduct[] = ['NEXORA', 'KULALI', 'PENTAFLOW'];
const OBJECTIVES: Objective[] = ['LEAD_GENERATION', 'TRAFFIC', 'ENGAGEMENT'];
const CTA_TYPES = ['LEARN_MORE', 'SIGN_UP', 'CONTACT_US'];
const LEAD_FIELD_TYPES = ['FULL_NAME', 'FIRST_NAME', 'LAST_NAME', 'EMAIL', 'PHONE', 'CITY', 'STATE', 'ZIP', 'COUNTRY', 'CUSTOM'];

const STATUS_STYLE: Record<string, { color: string; bg: string }> = {
  DRAFT: { color: '#D97706', bg: 'rgba(245,158,11,0.1)' },
  CREATED: { color: '#059669', bg: 'rgba(16,185,129,0.1)' },
  FAILED: { color: '#EF4444', bg: 'rgba(239,68,68,0.1)' },
  IMPORTED: { color: '#1A6CF6', bg: 'rgba(26,108,246,0.1)' },
  DELETED: { color: '#94A3B8', bg: 'rgba(148,163,184,0.12)' },
};

const fmt = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

/** ISO timestamp -> the local "YYYY-MM-DDTHH:mm" format a datetime-local input expects. */
const isoToDatetimeLocal = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/* ── Secret input (toggle-able masking, so pasted values can be checked) ──── */

const SecretInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
}> = ({ value, onChange }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        className="nx-input"
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{ paddingRight: 32, width: '100%' }}
      />
      <button
        type="button"
        onClick={() => setVisible(v => !v)}
        aria-label={visible ? 'Hide value' : 'Show value'}
        style={{
          position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
          background: 'none', border: 'none', cursor: 'pointer', padding: 2,
          color: 'var(--nx-text-muted)', display: 'flex',
        }}
      >
        {visible ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
};

/* ── Product Config modal ───────────────────────────────────────────────── */

const ProductConfigModal: React.FC<{
  config: MetaProductConfigDTO;
  onClose: () => void;
  onSaved: () => void;
}> = ({ config, onClose, onSaved }) => {
  const [appId, setAppId] = useState(config.appId ?? '');
  const [adAccountId, setAdAccountId] = useState(config.adAccountId ?? '');
  const [pageId, setPageId] = useState(config.pageId ?? '');
  const [accessToken, setAccessToken] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [webhookVerifyToken, setWebhookVerifyToken] = useState('');
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState(config.privacyPolicyUrl ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      await api.put(`/api/v1/admin/meta/products/${config.product}`, {
        appId: appId || undefined,
        adAccountId: adAccountId || undefined,
        pageId: pageId || undefined,
        accessToken: accessToken || undefined,
        appSecret: appSecret || undefined,
        webhookVerifyToken: webhookVerifyToken || undefined,
        privacyPolicyUrl: privacyPolicyUrl || undefined,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Failed to save configuration.'));
    } finally { setSaving(false); }
  };

  return (
    <Modal title={`Configure ${config.product}`} onClose={onClose} onSubmit={handleSubmit} isLoading={saving} error={error} submitLabel="Save">
      <label style={LABEL}>App ID<input className="nx-input" value={appId} onChange={e => setAppId(e.target.value)} /></label>
      <label style={LABEL}>Ad Account ID<input className="nx-input" placeholder="act_..." value={adAccountId} onChange={e => setAdAccountId(e.target.value)} /></label>
      <label style={LABEL}>Page ID<input className="nx-input" value={pageId} onChange={e => setPageId(e.target.value)} /></label>
      <label style={LABEL}>
        Access Token {config.accessTokenSet && <span style={{ color: '#059669', fontWeight: 400 }}>(already set — leave blank to keep)</span>}
        <SecretInput value={accessToken} onChange={setAccessToken} />
      </label>
      <label style={LABEL}>
        App Secret {config.appSecretSet && <span style={{ color: '#059669', fontWeight: 400 }}>(already set — leave blank to keep)</span>}
        <SecretInput value={appSecret} onChange={setAppSecret} />
      </label>
      <label style={LABEL}>
        Webhook Verify Token {config.webhookVerifyTokenSet && <span style={{ color: '#059669', fontWeight: 400 }}>(already set — leave blank to keep)</span>}
        <SecretInput value={webhookVerifyToken} onChange={setWebhookVerifyToken} />
      </label>
      <label style={LABEL}>Privacy Policy URL<input className="nx-input" value={privacyPolicyUrl} onChange={e => setPrivacyPolicyUrl(e.target.value)} /></label>
    </Modal>
  );
};

/* ── Import existing lead form modal ────────────────────────────────────── */

const ImportLeadFormModal: React.FC<{
  onClose: () => void;
  onImported: () => void;
}> = ({ onClose, onImported }) => {
  const [product, setProduct] = useState<MetaProduct>('NEXORA');
  const [formId, setFormId] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [metaCampaignId, setMetaCampaignId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      await api.post('/api/v1/admin/meta/lead-forms/import', {
        product, formId, campaignName, metaCampaignId: metaCampaignId || undefined,
      });
      onImported();
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Failed to import lead form.'));
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Import Existing Lead Form" onClose={onClose} onSubmit={handleSubmit} isLoading={saving} error={error} submitLabel="Import & Sync">
      <p style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginBottom: 4 }}>
        For a lead form created directly in Meta Ads Manager (not through this page). This pulls in its
        existing leads now and keeps syncing new ones going forward.
      </p>
      <label style={LABEL}>
        Product
        <select className="nx-input" value={product} onChange={e => setProduct(e.target.value as MetaProduct)}>
          {PRODUCTS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </label>
      <label style={LABEL}>
        Lead Form ID
        <input className="nx-input" placeholder="e.g. 123456789012345" value={formId} onChange={e => setFormId(e.target.value)} />
      </label>
      <label style={LABEL}>
        Label (for your reference)
        <input className="nx-input" placeholder="e.g. Instagram lead ad — August" value={campaignName} onChange={e => setCampaignName(e.target.value)} />
      </label>
      <label style={LABEL}>
        Meta Campaign ID <span style={{ fontWeight: 400 }}>(optional — enables the Ads Manager link)</span>
        <input className="nx-input" placeholder="e.g. 120210000000000" value={metaCampaignId} onChange={e => setMetaCampaignId(e.target.value)} />
      </label>
    </Modal>
  );
};

/* ── Delete campaign modal (email OTP gated) ────────────────────────────── */

const DeleteCampaignModal: React.FC<{
  campaign: MetaCampaignDTO;
  onClose: () => void;
  onDeleted: () => void;
}> = ({ campaign, onClose, onDeleted }) => {
  const [step, setStep] = useState<'confirm' | 'otp'>('confirm');
  const [otp, setOtp] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleRequestOtp = async () => {
    setError(''); setSaving(true);
    try {
      await api.post(`/api/v1/admin/meta/campaigns/${campaign.id}/delete/request-otp`);
      setStep('otp');
    } catch (err) {
      setError(errorMessage(err, 'Failed to send confirmation code.'));
    } finally { setSaving(false); }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      await api.post(`/api/v1/admin/meta/campaigns/${campaign.id}/delete/confirm`, { otp });
      onDeleted();
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Failed to delete campaign.'));
    } finally { setSaving(false); }
  };

  if (step === 'confirm') {
    return (
      <Modal
        title="Delete Campaign"
        onClose={onClose}
        onSubmit={e => { e.preventDefault(); handleRequestOtp(); }}
        isLoading={saving}
        error={error}
        submitLabel="Send confirmation code"
      >
        <p style={{ fontSize: 13, color: 'var(--nx-text-primary)' }}>
          This permanently deletes <strong>{campaign.campaignName}</strong> on Meta (status set to DELETED — ads stop
          running immediately). This cannot be undone.
        </p>
        <p style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
          For safety, we'll email a confirmation code to your admin account before deleting.
        </p>
      </Modal>
    );
  }

  return (
    <Modal title="Confirm Deletion" onClose={onClose} onSubmit={handleConfirm} isLoading={saving} error={error} submitLabel="Delete Campaign">
      <p style={{ fontSize: 13, color: 'var(--nx-text-primary)' }}>
        We emailed a 6-digit code to your admin account. Enter it below to permanently delete{' '}
        <strong>{campaign.campaignName}</strong>.
      </p>
      <label style={LABEL}>
        Confirmation code
        <input className="nx-input" required maxLength={6} value={otp} onChange={e => setOtp(e.target.value)} autoFocus />
      </label>
    </Modal>
  );
};

/* ── Email Draft modal ──────────────────────────────────────────────────── */

const EmailDraftModal: React.FC<{
  campaign: MetaCampaignDTO;
  onClose: () => void;
  onSaved: (updated: MetaCampaignDTO) => void;
}> = ({ campaign, onClose, onSaved }) => {
  const [subject, setSubject] = useState(campaign.emailSubject ?? '');
  const [body, setBody] = useState(campaign.emailBodyHtml ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const res = await api.put(`/api/v1/admin/meta/campaigns/${campaign.id}/email-draft`, {
        emailSubject: subject, emailBodyHtml: body,
      });
      onSaved(unwrap<MetaCampaignDTO>(res.data));
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Failed to save the email draft.'));
    } finally { setSaving(false); }
  };

  return (
    <Modal
      title={`Email Draft — ${campaign.campaignName}`}
      onClose={onClose}
      onSubmit={handleSubmit}
      isLoading={saving}
      error={error}
      submitLabel="Save Draft"
    >
      <p style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
        Used for "Send Thank You" emails to leads from this campaign. Use <code>{'{{name}}'}</code> in the body to
        insert the lead's name. Plain text is fine — leave a blank line between paragraphs and they'll be
        formatted automatically; or write raw HTML if you need more control.
      </p>
      <label style={LABEL}>
        Subject
        <input className="nx-input" required value={subject} onChange={e => setSubject(e.target.value)} autoFocus />
      </label>
      <label style={LABEL}>
        Body
        <textarea
          className="nx-input" required rows={10}
          style={{ fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
          value={body} onChange={e => setBody(e.target.value)}
        />
      </label>
    </Modal>
  );
};

/* ── Create Campaign modal ──────────────────────────────────────────────── */

const CreateCampaignModal: React.FC<{
  onClose: () => void;
  onCreated: (res: CampaignCreateResponse) => void;
  duplicateFrom?: DuplicateCampaignPayload;
}> = ({ onClose, onCreated, duplicateFrom: d }) => {
  const [product, setProduct] = useState<MetaProduct>(d?.product ?? 'NEXORA');
  const [objective, setObjective] = useState<Objective>((d?.objective as Objective) ?? 'LEAD_GENERATION');

  const [budgetType, setBudgetType] = useState<'daily' | 'lifetime'>(d?.budget.lifetimeInr ? 'lifetime' : 'daily');
  const [budgetAmount, setBudgetAmount] = useState(String(d?.budget.dailyInr ?? d?.budget.lifetimeInr ?? ''));

  const [locations, setLocations] = useState<LocationDraft[]>(
    d?.targeting.locations.length
      ? d.targeting.locations.map(l => ({ city: l.city, state: l.state ?? '', radiusKm: String(l.radiusKm ?? '25') }))
      : [{ city: '', state: '', radiusKm: '25' }]
  );
  const [gender, setGender] = useState<'ALL' | 'MALE' | 'FEMALE'>((d?.targeting.gender as 'ALL' | 'MALE' | 'FEMALE') ?? 'ALL');
  const [ageMin, setAgeMin] = useState(String(d?.targeting.ageMin ?? '21'));
  const [ageMax, setAgeMax] = useState(String(d?.targeting.ageMax ?? '55'));
  const [interestQuery, setInterestQuery] = useState('');
  const [interestResults, setInterestResults] = useState<InterestSearchResultDTO[]>([]);
  const [selectedInterests, setSelectedInterests] = useState<InterestSearchResultDTO[]>(
    d?.targeting.interestIds?.map(id => ({ id, name: id })) ?? []
  );
  const [searchingInterests, setSearchingInterests] = useState(false);

  const [startAt, setStartAt] = useState(isoToDatetimeLocal(d?.schedule.startAt));
  const [endAt, setEndAt] = useState(isoToDatetimeLocal(d?.schedule.endAt));

  const [primaryText, setPrimaryText] = useState(d?.creative.primaryText ?? '');
  const [headline, setHeadline] = useState(d?.creative.headline ?? '');
  const [description, setDescription] = useState(d?.creative.description ?? '');
  const [ctaType, setCtaType] = useState(d?.creative.ctaType ?? CTA_TYPES[0]);
  const [assetRef, setAssetRef] = useState(d?.creative.assetRef ?? '');
  const [destinationType, setDestinationType] = useState<DestinationType>(
    (d?.creative.destinationType as DestinationType) ?? 'LEAD_FORM'
  );
  const [destinationUrl, setDestinationUrl] = useState(d?.creative.destinationUrl ?? '');

  const [placementMode, setPlacementMode] = useState<PlacementMode>(
    (d?.placement?.mode as PlacementMode) ?? 'ADVANTAGE_PLUS'
  );

  const [adFormat, setAdFormat] = useState('MOBILE_FEED_STANDARD');
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const [formName, setFormName] = useState(d?.leadForm?.formName ?? '');
  const [formPrivacyUrl, setFormPrivacyUrl] = useState(d?.leadForm?.privacyPolicyUrl ?? '');
  const [leadFields, setLeadFields] = useState<LeadFormFieldDraft[]>(
    d?.leadForm?.fields.length
      ? d.leadForm.fields.map(f => ({ type: f.type, question: f.question ?? '' }))
      : [
          { type: 'FULL_NAME', question: '' },
          { type: 'PHONE', question: '' },
          { type: 'EMAIL', question: '' },
        ]
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const interestSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (interestSearchTimer.current) clearTimeout(interestSearchTimer.current);
    if (!interestQuery.trim()) { setInterestResults([]); return; }
    interestSearchTimer.current = setTimeout(async () => {
      setSearchingInterests(true);
      try {
        const res = await api.get('/api/v1/admin/meta/interests/search', { params: { product, q: interestQuery } });
        setInterestResults(unwrap<InterestSearchResultDTO[]>(res.data) ?? []);
      } catch { setInterestResults([]); }
      finally { setSearchingInterests(false); }
    }, 400);
    return () => { if (interestSearchTimer.current) clearTimeout(interestSearchTimer.current); };
  }, [interestQuery, product]);

  const addInterest = (interest: InterestSearchResultDTO) => {
    if (!selectedInterests.some(i => i.id === interest.id)) {
      setSelectedInterests([...selectedInterests, interest]);
    }
    setInterestQuery('');
    setInterestResults([]);
  };
  const removeInterest = (id: string) => setSelectedInterests(selectedInterests.filter(i => i.id !== id));

  const updateLeadField = (idx: number, patch: Partial<LeadFormFieldDraft>) => {
    setLeadFields(leadFields.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };
  const addLeadField = () => setLeadFields([...leadFields, { type: 'CUSTOM', question: '' }]);
  const removeLeadField = (idx: number) => setLeadFields(leadFields.filter((_, i) => i !== idx));

  const updateLocation = (idx: number, patch: Partial<LocationDraft>) => {
    setLocations(locations.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const addLocation = () => setLocations([...locations, { city: '', state: '', radiusKm: '25' }]);
  const removeLocation = (idx: number) => setLocations(locations.filter((_, i) => i !== idx));

  const buildCreativePayload = () => ({
    primaryText,
    headline,
    description: description || undefined,
    ctaType,
    assetRef: assetRef || undefined,
    destinationType,
    destinationUrl: destinationType === 'LANDING_URL' ? destinationUrl : undefined,
  });

  const handlePreview = async () => {
    setPreviewError(''); setPreviewSrc(null); setPreviewLoading(true);
    try {
      const res = await api.post('/api/v1/admin/meta/ad-preview', {
        product, creative: buildCreativePayload(), adFormat,
      });
      const { html } = unwrap<{ html: string }>(res.data);
      const match = html?.match(/src="([^"]+)"/);
      if (match) setPreviewSrc(match[1]);
      else setPreviewError('Meta returned a preview but it could not be rendered.');
    } catch (err) {
      setPreviewError(errorMessage(err, 'Failed to generate preview. Fill in primary text, headline and CTA first.'));
    } finally { setPreviewLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const payload = {
        product,
        objective,
        budget: {
          dailyInr: budgetType === 'daily' ? Number(budgetAmount) : undefined,
          lifetimeInr: budgetType === 'lifetime' ? Number(budgetAmount) : undefined,
        },
        targeting: {
          locations: locations
            .filter(l => l.city.trim())
            .map(l => ({ city: l.city, state: l.state || undefined, radiusKm: l.radiusKm ? Number(l.radiusKm) : undefined })),
          ageMin: ageMin ? Number(ageMin) : undefined,
          ageMax: ageMax ? Number(ageMax) : undefined,
          gender,
          interestIds: selectedInterests.map(i => i.id),
        },
        schedule: {
          startAt: startAt ? new Date(startAt).toISOString() : undefined,
          endAt: endAt ? new Date(endAt).toISOString() : undefined,
        },
        creative: buildCreativePayload(),
        placement: { mode: placementMode },
        leadForm: destinationType === 'LEAD_FORM'
          ? {
            product,
            formName,
            fields: leadFields.map(f => ({ type: f.type, question: f.type === 'CUSTOM' ? f.question : undefined })),
            privacyPolicyUrl: formPrivacyUrl || undefined,
          }
          : undefined,
      };
      const res = await api.post('/api/v1/admin/meta/campaigns', payload);
      onCreated(unwrap<CampaignCreateResponse>(res.data));
    } catch (err) {
      setError(errorMessage(err, 'Failed to create campaign. Please check the fields and try again.'));
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(8,14,29,0.75)', backdropFilter: 'blur(4px)' }} />
      <div style={{
        position: 'relative', zIndex: 1, width: '100%', maxWidth: 720,
        background: 'var(--nx-surface-1)', borderRadius: 16, border: '1px solid var(--nx-border)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', maxHeight: '90vh',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--nx-border)' }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)' }}>New Meta Campaign</h2>
          <button type="button" onClick={onClose} style={{ width: 28, height: 28, border: 'none', background: 'var(--nx-surface-3)', borderRadius: 7, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--nx-text-muted)' }}>
            <XIcon size={14} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '20px 24px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && (
            <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, fontSize: 12, color: '#EF4444' }}>
              {error}
            </div>
          )}

          <Section title="Basics">
            <Row>
              <label style={LABEL}>Product
                <select className="nx-input" value={product} onChange={e => setProduct(e.target.value as MetaProduct)}>
                  {PRODUCTS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label style={LABEL}>Objective
                <select className="nx-input" value={objective} onChange={e => setObjective(e.target.value as Objective)}>
                  {OBJECTIVES.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </label>
            </Row>
          </Section>

          <Section title="Budget">
            <Row>
              <label style={LABEL}>Type
                <select className="nx-input" value={budgetType} onChange={e => setBudgetType(e.target.value as 'daily' | 'lifetime')}>
                  <option value="daily">Daily</option>
                  <option value="lifetime">Lifetime</option>
                </select>
              </label>
              <label style={LABEL}>Amount (INR)<input className="nx-input" type="number" min="1" required value={budgetAmount} onChange={e => setBudgetAmount(e.target.value)} /></label>
            </Row>
          </Section>

          <Section title="Targeting">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {locations.map((loc, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input className="nx-input" placeholder="City" required value={loc.city} onChange={e => updateLocation(idx, { city: e.target.value })} />
                  <input className="nx-input" placeholder="State (optional)" value={loc.state} onChange={e => updateLocation(idx, { state: e.target.value })} />
                  <input className="nx-input" style={{ flex: '0 0 110px' }} type="number" placeholder="Radius (km)" value={loc.radiusKm} onChange={e => updateLocation(idx, { radiusKm: e.target.value })} />
                  <button type="button" className="nx-btn nx-btn-outline nx-btn-sm" onClick={() => removeLocation(idx)} disabled={locations.length === 1}>Remove</button>
                </div>
              ))}
              <button type="button" className="nx-btn nx-btn-outline nx-btn-sm" style={{ alignSelf: 'flex-start' }} onClick={addLocation}>+ Add location</button>
            </div>
            <Row>
              <label style={LABEL}>Age Min<input className="nx-input" type="number" min="13" max="65" value={ageMin} onChange={e => setAgeMin(e.target.value)} /></label>
              <label style={LABEL}>Age Max<input className="nx-input" type="number" min="13" max="65" value={ageMax} onChange={e => setAgeMax(e.target.value)} /></label>
              <label style={LABEL}>Gender
                <select className="nx-input" value={gender} onChange={e => setGender(e.target.value as 'ALL' | 'MALE' | 'FEMALE')}>
                  <option value="ALL">All genders</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                </select>
              </label>
            </Row>
            <label style={LABEL}>
              Interests / Behaviors
              <div style={{ position: 'relative' }}>
                <Search size={13} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--nx-text-muted)' }} />
                <input
                  className="nx-input" style={{ paddingLeft: 30 }}
                  placeholder="e.g. RWA, society management, real estate"
                  value={interestQuery} onChange={e => setInterestQuery(e.target.value)}
                />
              </div>
            </label>
            {searchingInterests && <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>Searching…</div>}
            {interestResults.length > 0 && (
              <div style={{ border: '1px solid var(--nx-border)', borderRadius: 8, maxHeight: 140, overflowY: 'auto' }}>
                {interestResults.map(r => (
                  <div key={r.id} onClick={() => addInterest(r)}
                    style={{ padding: '8px 12px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid var(--nx-border)' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--nx-surface-2)')}
                    onMouseLeave={e => (e.currentTarget.style.background = '')}
                  >
                    {r.name}
                  </div>
                ))}
              </div>
            )}
            {selectedInterests.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {selectedInterests.map(i => (
                  <span key={i.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 20, background: 'rgba(26,108,246,0.1)', color: '#1A6CF6', fontSize: 11, fontWeight: 600 }}>
                    {i.name}
                    <XIcon size={11} style={{ cursor: 'pointer' }} onClick={() => removeInterest(i.id)} />
                  </span>
                ))}
              </div>
            )}
            {d?.targeting.interestIds?.length ? (
              <p style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>
                Duplicated interests show raw IDs, not names — re-search and re-add if you want to verify or change them.
              </p>
            ) : null}
          </Section>

          <Section title="Schedule">
            <Row>
              <label style={LABEL}>Start<input className="nx-input" type="datetime-local" required value={startAt} onChange={e => setStartAt(e.target.value)} /></label>
              <label style={LABEL}>End (optional)<input className="nx-input" type="datetime-local" value={endAt} onChange={e => setEndAt(e.target.value)} /></label>
            </Row>
          </Section>

          <Section title="Creative">
            <label style={LABEL}>Primary Text<textarea className="nx-input" rows={3} required value={primaryText} onChange={e => setPrimaryText(e.target.value)} /></label>
            <Row>
              <label style={LABEL}>Headline<input className="nx-input" required value={headline} onChange={e => setHeadline(e.target.value)} /></label>
              <label style={LABEL}>Call to Action
                <select className="nx-input" value={ctaType} onChange={e => setCtaType(e.target.value)}>
                  {CTA_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </Row>
            <label style={LABEL}>Description (optional)<input className="nx-input" placeholder="Short line shown under the headline" value={description} onChange={e => setDescription(e.target.value)} /></label>
            <label style={LABEL}>Image/Video Asset ID (optional)<input className="nx-input" placeholder="Meta image hash or video ID" value={assetRef} onChange={e => setAssetRef(e.target.value)} /></label>
            <Row>
              <label style={LABEL}>Destination
                <select className="nx-input" value={destinationType} onChange={e => setDestinationType(e.target.value as DestinationType)}>
                  <option value="LEAD_FORM">Instant Lead Form</option>
                  <option value="LANDING_URL">Landing Page URL</option>
                </select>
              </label>
              <label style={LABEL}>Placement
                <select className="nx-input" value={placementMode} onChange={e => setPlacementMode(e.target.value as PlacementMode)}>
                  <option value="ADVANTAGE_PLUS">Automatic (Advantage+)</option>
                  <option value="MANUAL_FEED_ONLY">Manual — Feed Only</option>
                </select>
              </label>
            </Row>
            {destinationType === 'LANDING_URL' && (
              <label style={LABEL}>Destination URL<input className="nx-input" type="url" required placeholder="https://..." value={destinationUrl} onChange={e => setDestinationUrl(e.target.value)} /></label>
            )}

            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
              <label style={LABEL}>Preview Format
                <select className="nx-input" value={adFormat} onChange={e => setAdFormat(e.target.value)}>
                  {AD_FORMATS.map(f => <option key={f} value={f}>{f.replace(/_/g, ' ')}</option>)}
                </select>
              </label>
              <button type="button" className="nx-btn nx-btn-outline nx-btn-sm" onClick={handlePreview} disabled={previewLoading || !primaryText || !headline}>
                {previewLoading ? 'Loading…' : 'Preview Ad'}
              </button>
            </div>
            {previewError && (
              <div style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, fontSize: 12, color: '#EF4444' }}>
                {previewError}
              </div>
            )}
            {previewSrc && (
              <iframe
                title="Ad preview" src={previewSrc}
                style={{ width: '100%', height: 400, border: '1px solid var(--nx-border)', borderRadius: 8 }}
              />
            )}
          </Section>

          {destinationType === 'LEAD_FORM' && (
            <Section title="Instant Lead Form">
              <label style={LABEL}>Form Name<input className="nx-input" required value={formName} onChange={e => setFormName(e.target.value)} /></label>
              <label style={LABEL}>Privacy Policy URL (optional — falls back to product config)<input className="nx-input" value={formPrivacyUrl} onChange={e => setFormPrivacyUrl(e.target.value)} /></label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {leadFields.map((f, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select className="nx-input" style={{ flex: '0 0 160px' }} value={f.type} onChange={e => updateLeadField(idx, { type: e.target.value })}>
                      {LEAD_FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    {f.type === 'CUSTOM' && (
                      <input className="nx-input" placeholder="Custom question" value={f.question} onChange={e => updateLeadField(idx, { question: e.target.value })} />
                    )}
                    <button type="button" className="nx-btn nx-btn-outline nx-btn-sm" onClick={() => removeLeadField(idx)}>Remove</button>
                  </div>
                ))}
                <button type="button" className="nx-btn nx-btn-outline nx-btn-sm" style={{ alignSelf: 'flex-start' }} onClick={addLeadField}>+ Add field</button>
              </div>
            </Section>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4 }}>
            <button type="button" onClick={onClose} className="nx-btn nx-btn-outline nx-btn-sm">Cancel</button>
            <button type="submit" disabled={saving} className="nx-btn nx-btn-primary nx-btn-sm" style={{ opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Creating…' : 'Create Campaign (Paused)'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--nx-text-muted)' }}>{title}</div>
    {children}
  </div>
);
const Row: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', gap: 12 }}>{children}</div>
);
const LABEL: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--nx-text-primary)', flex: 1 };

/* ── MetaAdsPage ─────────────────────────────────────────────────────────── */

const MetaAdsPage: React.FC = () => {
  const [tab, setTab] = useState<'campaigns' | 'products'>('campaigns');

  const [campaigns, setCampaigns] = useState<MetaCampaignDTO[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [productFilter, setProductFilter] = useState<MetaProduct | ''>('');
  const [showCreate, setShowCreate] = useState(false);
  const [duplicateFrom, setDuplicateFrom] = useState<DuplicateCampaignPayload | undefined>(undefined);
  const [showImport, setShowImport] = useState(false);
  const [deletingCampaign, setDeletingCampaign] = useState<MetaCampaignDTO | null>(null);
  const [draftCampaign, setDraftCampaign] = useState<MetaCampaignDTO | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [createdResult, setCreatedResult] = useState<CampaignCreateResponse | null>(null);

  const [configs, setConfigs] = useState<MetaProductConfigDTO[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(true);
  const [editingConfig, setEditingConfig] = useState<MetaProductConfigDTO | null>(null);

  const fetchCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const res = await api.get('/api/v1/admin/meta/campaigns');
      setCampaigns(unwrap<MetaCampaignDTO[]>(res.data) ?? []);
    } catch { /* empty state shown */ } finally { setLoadingCampaigns(false); }
  };

  const fetchConfigs = async () => {
    setLoadingConfigs(true);
    try {
      const res = await api.get('/api/v1/admin/meta/products');
      setConfigs(unwrap<MetaProductConfigDTO[]>(res.data) ?? []);
    } catch { /* empty state shown */ } finally { setLoadingConfigs(false); }
  };

  useEffect(() => { fetchCampaigns(); fetchConfigs(); }, []);

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      await api.post('/api/v1/admin/meta/campaigns/sync-now');
      await fetchCampaigns();
    } catch { /* transient Meta API errors are logged server-side; just leave state as-is */ }
    finally { setSyncing(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/api/v1/admin/meta/campaigns/export', {
        params: { product: productFilter || undefined },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `meta-campaigns-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch { /* leave state as-is; user can retry */ }
    finally { setExporting(false); }
  };

  const handleDuplicate = (c: MetaCampaignDTO) => {
    if (!c.requestPayload) return;
    try {
      setDuplicateFrom(JSON.parse(c.requestPayload) as DuplicateCampaignPayload);
      setShowCreate(true);
    } catch { /* malformed payload — Duplicate button shouldn't have been shown */ }
  };

  const columns: GridColumn<MetaCampaignDTO>[] = [
    { key: 'campaignName', header: 'Campaign', render: c => <span style={{ fontWeight: 600 }}>{c.campaignName}</span> },
    {
      key: 'type', header: 'Type', render: c => (
        <span style={{
          padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700,
          color: c.metaCampaignId ? '#1A6CF6' : '#7C3AED',
          background: c.metaCampaignId ? 'rgba(26,108,246,0.1)' : 'rgba(124,58,237,0.1)',
        }}>
          {c.metaCampaignId ? 'Campaign' : 'Lead Form'}
        </span>
      ),
    },
    { key: 'product', header: 'Product', render: c => c.product },
    { key: 'objective', header: 'Objective', render: c => c.objective },
    {
      key: 'status', header: 'Status', render: c => {
        const s = STATUS_STYLE[c.status] ?? STATUS_STYLE.DRAFT;
        return <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, color: s.color, background: s.bg }}>{c.status}</span>;
      },
    },
    { key: 'budget', header: 'Budget (INR)', render: c => c.dailyBudgetInr ? `${c.dailyBudgetInr}/day` : c.lifetimeBudgetInr ? `${c.lifetimeBudgetInr} lifetime` : '—' },
    { key: 'city', header: 'City', render: c => c.targetCity ?? '—' },
    { key: 'createdAt', header: 'Created', render: c => fmt(c.createdAt) },
    {
      key: 'link', header: '', render: c => c.metaCampaignId ? (
        <a
          href={`https://www.facebook.com/adsmanager/manage/campaigns?selected_campaign_ids=${c.metaCampaignId}`}
          target="_blank" rel="noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#1A6CF6', textDecoration: 'none' }}
        >
          Ads Manager <ExternalLink size={12} />
        </a>
      ) : null,
    },
    {
      key: 'actions', header: '', render: c => (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          {c.requestPayload && (
            <button
              className="nx-btn nx-btn-outline nx-btn-sm" style={{ padding: '4px 8px', fontSize: 11 }}
              onClick={() => handleDuplicate(c)} title="Duplicate this campaign as a new one"
            >
              <Copy size={12} />
            </button>
          )}
          {c.status !== 'DELETED' && (
            <button
              className="nx-btn nx-btn-outline nx-btn-sm" style={{ padding: '4px 8px', fontSize: 11 }}
              onClick={() => setDraftCampaign(c)} title="Edit email draft for this campaign"
            >
              <Mail size={12} />
            </button>
          )}
          {c.metaCampaignId && c.status !== 'DELETED' && (
            <button
              className="nx-btn nx-btn-outline nx-btn-sm" style={{ padding: '4px 8px', fontSize: 11, color: '#EF4444', borderColor: 'rgba(239,68,68,0.3)' }}
              onClick={() => setDeletingCampaign(c)} title="Delete this campaign"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ background: '#f8fafc', minHeight: '100%', padding: 32, fontFamily: 'var(--font-body)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px 16px', marginBottom: 20 }}>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 800, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Megaphone size={20} /> Meta Ads
          </h1>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginTop: 4 }}>
            Create Meta (Facebook/Instagram) ad campaigns — always launched paused for review.
          </p>
        </div>
        {tab === 'campaigns' && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button className="nx-btn nx-btn-outline nx-btn-sm" onClick={handleSyncNow} disabled={syncing}>
              <RefreshCw size={14} style={{ marginRight: 4, ...(syncing ? { animation: 'nx-spin 1s linear infinite' } : {}) }} />
              {syncing ? 'Syncing…' : 'Sync Now'}
            </button>
            <button className="nx-btn nx-btn-outline nx-btn-sm" onClick={handleExport} disabled={exporting}>
              <Download size={14} style={{ marginRight: 4 }} />
              {exporting ? 'Exporting…' : 'Export'}
            </button>
            <button className="nx-btn nx-btn-outline nx-btn-sm" onClick={() => setShowImport(true)}>
              Import Existing Lead Form
            </button>
            <button className="nx-btn nx-btn-primary nx-btn-sm" onClick={() => { setDuplicateFrom(undefined); setShowCreate(true); }}>
              <Plus size={14} style={{ marginRight: 4 }} /> New Campaign
            </button>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16, borderBottom: '1px solid var(--nx-border)' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['campaigns', 'products'] as const).map(t => (
            <button
              key={t} onClick={() => setTab(t)}
              style={{
                padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: 600,
                color: tab === t ? '#1A6CF6' : 'var(--nx-text-muted)',
                borderBottom: tab === t ? '2px solid #1A6CF6' : '2px solid transparent',
              }}
            >
              {t === 'campaigns' ? 'Campaigns' : 'Product Config'}
            </button>
          ))}
        </div>
        {tab === 'campaigns' && (
          <select
            className="nx-input"
            value={productFilter}
            onChange={e => setProductFilter(e.target.value as MetaProduct | '')}
            style={{ fontSize: 12, padding: '6px 10px', marginBottom: 8 }}
          >
            <option value="">All products</option>
            {PRODUCTS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
      </div>

      {tab === 'campaigns' && (
        <div style={{ background: 'var(--nx-surface-1)', borderRadius: 12, border: '1px solid var(--nx-border)', overflow: 'hidden' }}>
          <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--nx-text-muted)', borderBottom: '1px solid var(--nx-border)' }}>
            <strong>Campaign</strong> rows are ad campaigns (have an Ads Manager link). <strong>Lead Form</strong> rows are lead forms found on your Page that aren't tied to a specific campaign here yet — Meta doesn't expose that link cheaply, so they're tracked separately just to sync their leads.
          </div>
          <DataGrid
            columns={columns}
            rows={productFilter ? campaigns.filter(c => c.product === productFilter) : campaigns}
            rowKey={c => c.id}
            loading={loadingCampaigns}
            emptyText={productFilter ? `No ${productFilter} campaigns yet.` : 'No campaigns yet. Create one to get started.'}
          />
        </div>
      )}

      {tab === 'products' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {loadingConfigs ? (
            <div style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>Loading…</div>
          ) : configs.map(cfg => (
            <div key={cfg.product} style={{ background: 'var(--nx-surface-1)', border: '1px solid var(--nx-border)', borderRadius: 12, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>{cfg.product}</span>
                <span style={{
                  padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                  color: cfg.configured ? '#059669' : '#D97706',
                  background: cfg.configured ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
                }}>
                  {cfg.configured ? 'CONFIGURED' : 'INCOMPLETE'}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 12 }}>
                <span>Ad Account: {cfg.adAccountId || '—'}</span>
                <span>Page: {cfg.pageId || '—'}</span>
                <span>Access Token: {cfg.accessTokenSet ? 'Set' : 'Not set'}</span>
              </div>
              <button className="nx-btn nx-btn-outline nx-btn-sm" onClick={() => setEditingConfig(cfg)}>
                <Settings size={13} style={{ marginRight: 4 }} /> Configure
              </button>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateCampaignModal
          duplicateFrom={duplicateFrom}
          onClose={() => { setShowCreate(false); setDuplicateFrom(undefined); }}
          onCreated={res => { setCreatedResult(res); setShowCreate(false); setDuplicateFrom(undefined); fetchCampaigns(); }}
        />
      )}

      {showImport && (
        <ImportLeadFormModal
          onClose={() => setShowImport(false)}
          onImported={fetchCampaigns}
        />
      )}

      {deletingCampaign && (
        <DeleteCampaignModal
          campaign={deletingCampaign}
          onClose={() => setDeletingCampaign(null)}
          onDeleted={fetchCampaigns}
        />
      )}

      {draftCampaign && (
        <EmailDraftModal
          campaign={draftCampaign}
          onClose={() => setDraftCampaign(null)}
          onSaved={updated => setCampaigns(cs => cs.map(c => c.id === updated.id ? updated : c))}
        />
      )}

      {createdResult && (
        <Modal title="Campaign Created" onClose={() => setCreatedResult(null)} onSubmit={e => { e.preventDefault(); setCreatedResult(null); }} submitLabel="Done">
          <p style={{ fontSize: 13, color: 'var(--nx-text-primary)' }}>
            The campaign was created in <strong>PAUSED</strong> status. Review it in Ads Manager before activating.
          </p>
          <a href={createdResult.adsManagerUrl} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#1A6CF6' }}>
            Open in Ads Manager <ExternalLink size={13} />
          </a>
        </Modal>
      )}

      {editingConfig && (
        <ProductConfigModal config={editingConfig} onClose={() => setEditingConfig(null)} onSaved={fetchConfigs} />
      )}
    </div>
  );
};

export default MetaAdsPage;
